/**
 * Docs cache: the page tree (pages + databases the caller can see, rows excluded) and favourites, kept up to date by
 * page events. Pure reducers and selectors — the platform data layer stores the result (TanStack Query on web,
 * SQLiteData / SQLDelight natively). Page bodies are not here: they are Loro docs behind the EditorDoc port.
 */
import type { BootstrapResponse, ListPagesResponse, PageNode, PageVisibility, PersistentEvent, UpdatePageRequest } from "@bundle/contracts";
import { generateKeyBetween } from "fractional-indexing";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { pageBadge, type PageBadge } from "./sharing.ts";

export type DocsCache = {
  /** Visible pages and databases by id (rows and trashed pages are never kept). */
  pages: Record<string, PageNode>;
  /** My favourites, in the order I added them. */
  favoriteIds: string[];
};

export function emptyDocs(): DocsCache {
  return { pages: {}, favoriteIds: [] };
}

/** Template content (a saved template's pages, a row template's body) is never part of the tree. */
function inTree(p: PageNode): boolean {
  return p.kind !== "row" && p.archivedAt === undefined && p.templateId === undefined;
}

export function fromList(res: ListPagesResponse): DocsCache {
  const pages: Record<string, PageNode> = {};
  for (const p of res.pages) if (inTree(p)) pages[p.id] = p;
  return { pages, favoriteIds: res.favoritePageIds.filter((id) => pages[id] !== undefined) };
}

/** The tree from a bootstrap response; undefined from a pre-M5 server (no `pages`). */
export function fromBootstrap(b: BootstrapResponse): DocsCache | undefined {
  if (b.pages === undefined) return undefined;
  return fromList({ pages: b.pages, favoritePageIds: b.favoritePageIds ?? [] });
}

/** Removes pages and their whole subtrees (trash, lost access). */
export function removePages(cache: DocsCache, ids: string[]): DocsCache {
  const gone = new Set<string>();
  const queue = ids.filter((id) => cache.pages[id] !== undefined);
  while (queue.length > 0) {
    const id = queue.pop()!;
    if (gone.has(id)) continue;
    gone.add(id);
    for (const p of Object.values(cache.pages)) if (p.parentId === id && !gone.has(p.id)) queue.push(p.id);
  }
  if (gone.size === 0) return cache;
  const pages: Record<string, PageNode> = {};
  for (const [id, p] of Object.entries(cache.pages)) if (!gone.has(id)) pages[id] = p;
  return { pages, favoriteIds: cache.favoriteIds.filter((id) => !gone.has(id)) };
}

/** Inserts or replaces pages (a create response, a page event); trashed ones are removed with their subtrees. */
export function upsertPages(cache: DocsCache, list: PageNode[]): DocsCache {
  let pages: Record<string, PageNode> | undefined;
  const trashed: string[] = [];
  for (const p of list) {
    if (p.kind === "row" || p.templateId !== undefined) continue;
    if (p.archivedAt !== undefined) {
      trashed.push(p.id);
      continue;
    }
    pages ??= { ...cache.pages };
    pages[p.id] = p;
  }
  const next = pages === undefined ? cache : { ...cache, pages };
  return removePages(next, trashed);
}

/** Applies a persistent event (page.*, database.updated); everything else returns the same cache. */
export function applyEvent(cache: DocsCache, ev: PersistentEvent): DocsCache {
  switch (ev.type) {
    case "page.created":
    case "page.updated":
      return upsertPages(cache, [ev.page]);
    case "page.removed":
      return removePages(cache, [ev.pageId]);
    case "database.updated": {
      // The database's page node mirrors its title and icon.
      const p = cache.pages[ev.database.id];
      if (p === undefined) return cache;
      const next: PageNode = { ...p, title: ev.database.title };
      if (ev.database.icon === undefined) delete next.icon;
      else next.icon = ev.database.icon;
      return { ...cache, pages: { ...cache.pages, [p.id]: next } };
    }
    default:
      return cache;
  }
}

export function setFavorite(cache: DocsCache, pageId: string, on: boolean): DocsCache {
  const has = cache.favoriteIds.includes(pageId);
  if (on === has || (on && cache.pages[pageId] === undefined)) return cache;
  return { ...cache, favoriteIds: on ? [...cache.favoriteIds, pageId] : cache.favoriteIds.filter((id) => id !== pageId) };
}

// ---------- Tree ----------

function byRank(a: PageNode, b: PageNode): number {
  return a.rank < b.rank ? -1 : a.rank > b.rank ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** Root of the page's tree within what I can see (a page whose parent I can't see is a root). */
function isRoot(cache: DocsCache, p: PageNode): boolean {
  return p.parentId === undefined || cache.pages[p.parentId] === undefined;
}

/** Children of `parentId` in rank order; absent ⇒ the top level of `visibility`. */
export function childrenOf(cache: DocsCache, parentId: string | undefined, visibility: PageVisibility): PageNode[] {
  const out: PageNode[] = [];
  for (const p of Object.values(cache.pages)) {
    if (parentId === undefined ? isRoot(cache, p) && p.visibility === visibility : p.parentId === parentId) out.push(p);
  }
  return out.toSorted(byRank);
}

export function hasChildren(cache: DocsCache, pageId: string): boolean {
  for (const p of Object.values(cache.pages)) if (p.parentId === pageId) return true;
  return cache.pages[pageId]?.hasChildren === true && cache.pages[pageId]?.kind === "page";
}

/** Ancestors from the top down to the parent (breadcrumbs). */
export function ancestors(cache: DocsCache, pageId: string): PageNode[] {
  const out: PageNode[] = [];
  const seen = new Set<string>([pageId]);
  let p = cache.pages[pageId]?.parentId;
  while (p !== undefined && !seen.has(p)) {
    const node = cache.pages[p];
    if (node === undefined) break;
    seen.add(p);
    out.unshift(node);
    p = node.parentId;
  }
  return out;
}

/** Is `ancestorId` `pageId` itself or above it? (a page can't move into its own subtree) */
export function isSelfOrAncestor(cache: DocsCache, ancestorId: string, pageId: string): boolean {
  if (ancestorId === pageId) return true;
  return ancestors(cache, pageId).some((a) => a.id === ancestorId);
}

export type PageTreeSectionId = "favorites" | "workspace" | "private" | "shared";

export type PageTreeRow = {
  /** Unique per row: `<section>/<pageId>` (a favourite also appears in its own section). */
  key: string;
  id: string;
  section: PageTreeSectionId;
  kind: PageNode["kind"];
  title: string;
  icon?: string;
  depth: number;
  /** Has (or may have) child pages: shows the disclosure triangle. */
  expandable: boolean;
  expanded: boolean;
  favorite: boolean;
  /** I can rename, move, trash and add subpages. */
  editable: boolean;
  /** Sharing indicator (logic/sharing#pageBadge): published, shared with people, or only people invited. */
  badge?: PageBadge;
};

export type PageTreeSection = { id: PageTreeSectionId; title: MessageDescriptor; rows: PageTreeRow[] };

/** Expansion is per row, so a page can be open under Favorites and closed in its section. */
export function rowKey(section: PageTreeSectionId, pageId: string): string {
  return `${section}/${pageId}`;
}

/**
 * Sidebar sections — Favorites, Workspace, Private, Shared with me — as flat rows in display order with depth,
 * expanding the rows whose keys are in `expanded`. Databases never expand (their rows are pages of the database, not
 * the tree). With `meId`, private pages other people shared with me go to "Shared with me" instead of Private.
 * Empty Favorites / Private / Shared sections are omitted; Workspace is always present.
 */
export function treeSections(cache: DocsCache, expanded: string[], meId?: string): PageTreeSection[] {
  const open = new Set(expanded);
  const fav = new Set(cache.favoriteIds);
  const rows = (section: PageTreeSectionId, list: PageNode[], depth: number, out: PageTreeRow[], seen: Set<string>) => {
    for (const p of list) {
      if (seen.has(p.id)) continue;
      const key = rowKey(section, p.id);
      const expandable = p.kind === "page" && hasChildren(cache, p.id);
      const isOpen = expandable && open.has(key);
      const row: PageTreeRow = {
        key,
        id: p.id,
        section,
        kind: p.kind,
        title: p.title,
        depth,
        expandable,
        expanded: isOpen,
        favorite: fav.has(p.id),
        editable: p.myAccess === "full" || p.myAccess === "edit",
      };
      if (p.icon !== undefined) row.icon = p.icon;
      const badge = pageBadge(p);
      if (badge !== undefined) row.badge = badge;
      out.push(row);
      if (isOpen) rows(section, childrenOf(cache, p.id, p.visibility), depth + 1, out, new Set([...seen, p.id]));
    }
  };
  const sections: PageTreeSection[] = [];
  const favorites: PageTreeRow[] = [];
  rows("favorites", cache.favoriteIds.map((id) => cache.pages[id]).filter((p): p is PageNode => p !== undefined), 0, favorites, new Set());
  if (favorites.length > 0) sections.push({ id: "favorites", title: msg("docs.section.favorites"), rows: favorites });
  const workspace: PageTreeRow[] = [];
  rows("workspace", childrenOf(cache, undefined, "workspace"), 0, workspace, new Set());
  sections.push({ id: "workspace", title: msg("docs.section.workspace"), rows: workspace });
  const privateRoots = childrenOf(cache, undefined, "private");
  const mine = (p: PageNode) => meId === undefined || p.createdBy === meId;
  const priv: PageTreeRow[] = [];
  rows("private", privateRoots.filter(mine), 0, priv, new Set());
  if (priv.length > 0) sections.push({ id: "private", title: msg("docs.section.private"), rows: priv });
  const shared: PageTreeRow[] = [];
  rows("shared", privateRoots.filter((p) => !mine(p)), 0, shared, new Set());
  if (shared.length > 0) sections.push({ id: "shared", title: msg("docs.section.shared"), rows: shared });
  return sections;
}

/** The sidebar section a page's tree lives in (with `meId`: others' private pages are "shared"). */
export function sectionOf(cache: DocsCache, pageId: string, meId?: string): PageTreeSectionId | undefined {
  const root = ancestors(cache, pageId)[0] ?? cache.pages[pageId];
  if (root === undefined) return undefined;
  if (root.visibility !== "private") return "workspace";
  return meId === undefined || root.createdBy === meId ? "private" : "shared";
}

/** Row keys to open so `pageId` shows in its section (its ancestors), e.g. after creating a subpage. */
export function keysToReveal(cache: DocsCache, pageId: string, meId?: string): string[] {
  const chain = ancestors(cache, pageId);
  const root = chain[0] ?? cache.pages[pageId];
  if (root === undefined) return [];
  const section: PageTreeSectionId = sectionOf(cache, pageId, meId) ?? "workspace";
  return chain.map((a) => rowKey(section, a.id));
}

/** Recently edited pages and databases, newest first (Home "Recent docs"). */
export function recent(cache: DocsCache, limit: number): PageNode[] {
  return Object.values(cache.pages)
    .toSorted((a, b) => b.updatedAt - a.updatedAt || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0))
    .slice(0, Math.max(0, limit));
}

/** Databases I can see, most recently edited first (Projects hub). */
export function databases(cache: DocsCache): PageNode[] {
  return Object.values(cache.pages)
    .filter((p) => p.kind === "database")
    .toSorted((a, b) => b.updatedAt - a.updatedAt || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
}

// ---------- Optimistic edits ----------

/**
 * Rank between two siblings (fractional-indexing; plain string order), as the server would assign it. Used for
 * optimistic moves; the server's page.updated replaces it.
 */
export function rankBetween(before: string | undefined, after: string | undefined): string {
  try {
    return generateKeyBetween(before ?? null, after ?? null);
  } catch {
    // Out-of-order or non-fractional ranks from elsewhere: append after `before`.
    return `${before ?? ""}V`;
  }
}

/**
 * Applies an UpdatePageRequest locally (title, icon, visibility, move, trash/restore) as the server will. A move
 * places the page after `afterId` (null/absent ⇒ first) under `parentId` (null ⇒ top level). Moves into the page's
 * own subtree are ignored.
 */
export function applyUpdate(cache: DocsCache, pageId: string, req: UpdatePageRequest, meId: string, now: number): DocsCache {
  const p = cache.pages[pageId];
  if (p === undefined) return cache;
  if (req.archived === true) return removePages(cache, [pageId]);
  const next: PageNode = { ...p, updatedBy: meId, updatedAt: now };
  if (req.title !== undefined) next.title = req.title;
  if (req.icon !== undefined) {
    if (req.icon === "") delete next.icon;
    else next.icon = req.icon;
  }
  if (req.visibility !== undefined) next.visibility = req.visibility;
  if (req.locked === true) next.locked = true;
  else if (req.locked === false) delete next.locked;
  const moving = req.parentId !== undefined || req.afterId !== undefined;
  if (moving) {
    const parentId = req.parentId === undefined ? p.parentId : req.parentId ?? undefined;
    if (parentId !== undefined && (cache.pages[parentId] === undefined || isSelfOrAncestor(cache, pageId, parentId))) return cache;
    if (parentId === undefined) delete next.parentId;
    else next.parentId = parentId;
    if (parentId !== undefined) next.visibility = cache.pages[parentId]!.visibility;
    const sibs = childrenOf(cache, parentId, next.visibility).filter((s) => s.id !== pageId);
    const afterIdx = req.afterId === undefined || req.afterId === null ? -1 : sibs.findIndex((s) => s.id === req.afterId);
    next.rank = rankBetween(sibs[afterIdx]?.rank, sibs[afterIdx + 1]?.rank);
  }
  return { ...cache, pages: { ...cache.pages, [pageId]: next } };
}

// ---------- Move to… ----------

export type MoveTarget = { id: string; title: string; icon?: string; depth: number; visibility: PageVisibility };

/**
 * Pages `pageId` can move under ("Move to…"): pages (not databases) I can edit, outside its own subtree, in sidebar
 * order (Workspace, then Private) with their depth; a query keeps the matching titles (flat). The page's current
 * parent is included (moving there just reorders).
 */
export function moveTargets(cache: DocsCache, pageId: string, query: string, limit: number): MoveTarget[] {
  const q = query.trim().toLowerCase();
  const out: MoveTarget[] = [];
  const walk = (list: PageNode[], depth: number) => {
    for (const p of list) {
      if (out.length >= limit) return;
      if (p.id === pageId || p.kind !== "page") continue;
      const editable = p.myAccess === "full" || p.myAccess === "edit";
      if (editable && (q === "" || p.title.toLowerCase().includes(q))) {
        const t: MoveTarget = { id: p.id, title: p.title, depth: q === "" ? depth : 0, visibility: p.visibility };
        if (p.icon !== undefined) t.icon = p.icon;
        out.push(t);
      }
      walk(childrenOf(cache, p.id, p.visibility), depth + 1);
    }
  };
  walk(childrenOf(cache, undefined, "workspace"), 0);
  walk(childrenOf(cache, undefined, "private"), 0);
  return out.slice(0, Math.max(0, limit));
}
